import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const useTextToSpeech = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [rate, setRate] = useState(1);
  const { i18n } = useTranslation();

  useEffect(() => {
    // Check if speech synthesis is supported
    if ('speechSynthesis' in window) {
      setIsSupported(true);
    }
  }, []);

  const speak = useCallback((text: string) => {
    if (!isSupported) {
      toast.error('Text-to-speech is not supported in your browser');
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    if (!text || text.trim() === '') {
      toast.error('No text to read');
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Set language based on current locale
    const langMap: { [key: string]: string } = {
      en: 'en-US',
      es: 'es-ES',
      fr: 'fr-FR',
      ko: 'ko-KR',
      tl: 'tl-PH',
      lou: 'fr-FR', // Louisiana Creole uses French voice
    };
    
    utterance.lang = langMap[i18n.language] || 'en-US';
    utterance.rate = rate;
    utterance.pitch = 1;
    utterance.volume = 1;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      setIsSpeaking(false);
      setIsPaused(false);
      toast.error('Failed to read text aloud');
    };

    window.speechSynthesis.speak(utterance);
  }, [isSupported, rate, i18n.language]);

  const pause = useCallback(() => {
    if (isSupported && isSpeaking && !isPaused) {
      window.speechSynthesis.pause();
      setIsPaused(true);
    }
  }, [isSupported, isSpeaking, isPaused]);

  const resume = useCallback(() => {
    if (isSupported && isSpeaking && isPaused) {
      window.speechSynthesis.resume();
      setIsPaused(false);
    }
  }, [isSupported, isSpeaking, isPaused]);

  const stop = useCallback(() => {
    if (isSupported) {
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      setIsPaused(false);
    }
  }, [isSupported]);

  const readPageContent = useCallback(() => {
    // Get main content
    const mainContent = document.getElementById('main-content');
    if (!mainContent) {
      toast.error('No content found to read');
      return;
    }

    // Extract text content, excluding navigation and footer
    const textContent = mainContent.innerText;
    speak(textContent);
  }, [speak]);

  const readSelectedText = useCallback(() => {
    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();

    if (!selectedText) {
      toast.error('Please select some text to read');
      return;
    }

    speak(selectedText);
  }, [speak]);

  return {
    isSupported,
    isSpeaking,
    isPaused,
    rate,
    setRate,
    speak,
    pause,
    resume,
    stop,
    readPageContent,
    readSelectedText,
  };
};
